import {
  REVENUE_REFRESH_NOTE,
  revenueCategoryReceiptLines,
  revenueRefreshOnly,
} from 'src/screens/venue-groups/runReceipt'

export const SHOPPING_LIST_REPLACES =
  'This replaces the whole shopping list. Lines that are not in the source are removed. This cannot be undone.'

const shoppingListSupplierDetail = row => {
  const count = Number(row.count) || 0
  const noun = count === 1 ? 'line needs' : 'lines need'
  const supplier = row.supplierTitle
    ? `${row.supplierTitle}, which is not switched on`
    : 'a supplier that is not switched on'
  return `${count} ${noun} ${supplier}. Switch that supplier on, then come back.`
}

export const shoppingListSupplierLines = rows =>
  (rows || []).map(row => `${row.title}: ${shoppingListSupplierDetail(row)}`)

export const shoppingListNeedsSupplier = rows =>
  (rows || []).some(row => Number(row.count) > 0)

const shoppingListSupplierActive = rows =>
  (rows || []).filter(row => (Number(row.count) || 0) > 0)

export const shoppingListSupplierSummary = rows => {
  const active = shoppingListSupplierActive(rows)
  if (!active.length) return ''
  const venueCount = new Set(active.map(row => row.title)).size
  const named = new Set(
    active.map(row => String(row.supplierTitle || '').trim()).filter(Boolean),
  )
  const unnamed = active.filter(
    row => !String(row.supplierTitle || '').trim(),
  ).length
  const supplierCount = named.size + unnamed
  const venueWord = venueCount === 1 ? 'venue' : 'venues'
  const supplierWord = supplierCount === 1 ? 'supplier' : 'suppliers'
  return `${venueCount} ${venueWord} · ${supplierCount} ${supplierWord} not on`
}

export const venueSuppliersPath = venueId =>
  venueId ? `/v/${venueId}/suppliers` : ''

export const venueShoppingListPath = venueId =>
  venueId ? `/v/${venueId}/shopping-lists` : ''

export const shoppingListSupplierTurnOn = row => {
  if (!row?.venueId || !row?.supplierId) return null
  const supplier = String(row.supplierTitle || '').trim() || 'this supplier'
  const venue = String(row.title || '').trim() || 'this venue'
  return {
    key: `${row.venueId}:${row.supplierId}`,
    venueId: String(row.venueId),
    supplierId: String(row.supplierId),
    href: venueSuppliersPath(row.venueId),
    label: `Turn on ${supplier} on ${venue}`,
  }
}

export const shoppingListSuppliersToEnable = rows => {
  const seen = new Set()
  const list = []
  for (const row of shoppingListSupplierActive(rows)) {
    const id = String(row.supplierId || '')
    if (!id || seen.has(id)) continue
    seen.add(id)
    list.push({
      supplierId: id,
      supplierTitle: String(row.supplierTitle || '').trim() || 'Supplier',
    })
  }
  return list
}

export const shoppingListSupplierVenueGroups = rows => {
  const active = shoppingListSupplierActive(rows)
  const order = []
  const byTitle = new Map()
  for (const row of active) {
    const title = row.title || 'Venue'
    if (!byTitle.has(title)) {
      byTitle.set(title, [])
      order.push(title)
    }
    byTitle.get(title).push(row)
  }
  return order.map(title => {
    const venueRows = byTitle.get(title)
    return {
      title,
      supplierCount: venueRows.length,
      lines: venueRows.map(shoppingListSupplierDetail),
      turnOns: venueRows.map(shoppingListSupplierTurnOn).filter(Boolean),
    }
  })
}

export const recipeGapVenueLinks = gaps => {
  const seen = new Set()
  const links = []
  for (const row of gaps || []) {
    const venueId = String(row?.venueId || '')
    if (!venueId || seen.has(venueId)) continue
    seen.add(venueId)
    const title = String(row?.title || '').trim() || 'this venue'
    links.push({
      key: venueId,
      venueId,
      href: venueShoppingListPath(venueId),
      suppliersHref: venueSuppliersPath(venueId),
      label: `Open shopping list on ${title}`,
      suppliersLabel: `Open suppliers on ${title}`,
    })
  }
  return links
}

const venueList = titles => {
  const names = (titles || [])
    .map(title => String(title || '').trim())
    .filter(Boolean)
  if (names.length <= 1) return names[0] || ''
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  return `${names.slice(0, -1).join(', ')}, and ${names.at(-1)}`
}

export const restrictedSupplierMissingLines = rows =>
  (rows || [])
    .filter(row => (row.missing || []).length > 0)
    .map(row => {
      const who = venueList(row.missing)
      const verb =
        (row.missing || []).length === 1 ? 'does not have' : 'do not have'
      return `${who} ${verb} ${row.supplierTitle || 'this supplier'}.`
    })

export const restrictedSupplierConfirmation = rows => {
  const list = rows || []
  if (!list.length) return 'No suppliers are ticked.'
  const firstMissing = venueList(list[0].missing)
  const firstLanding = venueList(list[0].landingVenues)
  const sameVenues =
    list.length > 1 &&
    list.every(row => venueList(row.missing) === firstMissing) &&
    list.every(row => venueList(row.landingVenues) === firstLanding) &&
    firstLanding
  if (sameVenues) {
    const suppliers = venueList(
      list.map(row => row.supplierTitle || 'This supplier'),
    )
    const verb =
      (list[0].missing || []).length === 1 ? 'does not have' : 'do not have'
    const missing = firstMissing ? `${firstMissing} ${verb} them.` : ''
    return [`${suppliers} will be restricted on ${firstLanding}.`, missing]
      .filter(Boolean)
      .join(' ')
  }
  return list
    .map(row => {
      const title = row.supplierTitle || 'This supplier'
      const landing = Number(row.landing) || 0
      const venueWord = landing === 1 ? 'venue' : 'venues'
      const restricted =
        landing === 0
          ? `${title} will not be restricted.`
          : `${title} will be restricted on ${landing} ${venueWord}.`
      const missing = restrictedSupplierMissingLines([row])[0] || ''
      return [restricted, missing].filter(Boolean).join(' ')
    })
    .join(' ')
}

export const restrictedApproverGaps = approvers =>
  (approvers || []).filter(
    row => !(row.emails || []).some(email => String(email).includes('@')),
  )

export const restrictedApproversBlockConfirm = ({
  approvers,
  allowWithoutApprover,
}) => restrictedApproverGaps(approvers).length > 0 && !allowWithoutApprover

export const restrictedSuppliersCannotLand = rows => {
  const list = rows || []
  return list.length > 0 && list.every(row => Number(row.landing) === 0)
}

export const VENUE_GROUP_ACTION_LABELS = {
  apply_shopping_list: 'Shopping list',
  replace_product: 'Replace product',
  share_recipes: 'Recipes',
  copy_restricted_suppliers: 'Restricted suppliers',
  invite_people: 'Invite',
  change_venue_person: 'Person',
  copy_menu: 'Menu',
  copy_recipe_categories: 'Categories',
  copy_allergen_flags: 'Allergens',
  copy_standing_orders: 'Standing orders',
  point_price_files: 'Price files',
  connect_supplier: 'Supplier',
  copy_business_days: 'Business days',
  copy_stock_count_sheet: 'Stock-count sheet',
  copy_home_task: 'Home task',
  assign_revenue_categories: 'Revenue categories',
  dummy: 'Run',
}

export const formatShareConfirmation = ({ action, updated, at, results }) => {
  const when = at
    ? new Date(at).toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : ''
  if (action === 'apply_shopping_list') {
    const rows = results || []
    const updatedCount = rows.length
      ? rows.filter(row => row.outcome === 'updated').length
      : Number(updated) || 0
    const skippedCount = rows.filter(row => row.outcome === 'skipped').length
    const failedCount = rows.filter(row => row.outcome === 'failed').length
    const totals = rows.length
      ? `${updatedCount} updated. ${skippedCount} skipped. ${failedCount} failed.`
      : `${updatedCount} updated.`
    return ['Shopping list shared.', totals, when].filter(Boolean).join(' ')
  }
  if (action === 'assign_revenue_categories') {
    const detail = revenueRefreshOnly(results)
      ? REVENUE_REFRESH_NOTE
      : revenueCategoryReceiptLines(results).join(' ')
    if (detail) return [detail, when].filter(Boolean).join(' ')
  }
  const label = VENUE_GROUP_ACTION_LABELS[action] || 'Change'
  const count = Number(updated) || 0
  return [`${label} shared.`, `${count} updated.`, when]
    .filter(Boolean)
    .join(' ')
}

const recipeSkipSummary = (gaps, excludesSource) => {
  const rows = gaps || []
  const venueCount = new Set(rows.map(row => row.title)).size
  const groups = new Map()
  for (const row of rows) {
    for (const item of row.recipes || []) {
      const recipe = item.recipe || ''
      if (!recipe) continue
      const venues = groups.get(recipe) || []
      if (!venues.includes(row.title)) venues.push(row.title)
      groups.set(recipe, venues)
    }
  }
  const parts = [...groups.entries()].map(([recipe, venues]) => {
    const everyTarget = venues.length === venueCount && venueCount > 1
    const who = everyTarget
      ? excludesSource
        ? `the other ${venueCount} venues`
        : `all ${venueCount} venues`
      : excludesSource
      ? venues.length === 1
        ? 'the other venue'
        : `${venues.length} of the other venues`
      : venues.length === 1
      ? venues[0]
      : `${venues.length} venues`
    return `${recipe} at ${who}`
  })
  if (parts.length === 0) return ''
  if (parts.length === 1) return `${parts[0]}.`
  const last = parts.pop()
  return `${parts.join(', ')}, and ${last}.`
}

const personList = lines => {
  if (lines.length === 1) return lines[0]
  if (lines.length === 2) return `${lines[0]} and ${lines[1]}`
  return `${lines.slice(0, -1).join(', ')}, and ${lines.at(-1)}`
}

export const inviteEmailConfirmation = ({
  email,
  roleLabel,
  knownAccount,
  venueTitles,
  leftVenues,
} = {}) => {
  const who = String(email || 'This email').trim()
  const role = String(roleLabel || 'their role').trim()
  const venues = (venueTitles || [])
    .map(title => String(title || '').trim())
    .filter(Boolean)
  const left = (leftVenues || [])
    .map(row => {
      const title = String(row?.title || row || '').trim()
      const why = String(row?.reason || 'they are already on it').trim()
      return title ? `${title} will be left because ${why}.` : ''
    })
    .filter(Boolean)
  const summary = 'One email is sent for the whole share, not one per venue.'
  const place = venues.length ? ` on ${personList(venues)}` : ''
  const leftLine = left.join(' ')
  if (knownAccount && venues.length === 0) {
    return [`${who} already has an OMS account.`, leftLine, summary]
      .filter(Boolean)
      .join(' ')
  }
  const action = knownAccount
    ? `${who} already has an OMS account and will be added as ${role}${place}.`
    : `${who} will get the new-person invite as ${role}${place}.`
  return [action, leftLine, summary].filter(Boolean).join(' ')
}

const roleRows = rows =>
  (rows || []).map(row =>
    typeof row === 'string'
      ? { title: row, currentRole: '' }
      : {
          title: String(row?.title || '').trim(),
          currentRole: String(row?.currentRole || '').trim(),
          reason: String(row?.reason || '').trim(),
        },
  )

export const changePersonRoleConfirmation = ({
  email,
  roleLabel,
  venueTitles,
  leftVenues,
} = {}) => {
  const who = String(email || 'This person').trim()
  const role = String(roleLabel || 'their role').trim()
  const changing = roleRows(venueTitles).filter(row => row.title)
  const leftRows = roleRows(leftVenues).filter(row => row.title)
  const venues = venueList(changing.map(row => row.title))
  const left = leftRows.map(row => {
    const why = `${row.title} will be left because ${
      row.reason || 'they are not on it'
    }.`
    return row.currentRole ? `${why} Role today: ${row.currentRole}.` : why
  })
  const now = changing
    .filter(row => row.currentRole)
    .map(row => `${row.title} is currently ${row.currentRole}.`)
  const change = venues
    ? `${who} will become ${role} on ${venues}.`
    : `${who} will not become ${role}.`
  const emailLine = venues
    ? 'One email is sent for the venues that change, not one per venue.'
    : 'No email is sent.'
  return [
    change,
    ...now,
    ...left,
    'A venue you do not tick is left.',
    emailLine,
    'A different role on other venues is another run.',
  ]
    .filter(Boolean)
    .join(' ')
}

export const PRIMARY_OWNER_REMOVAL_REASON = 'they are the primary owner'
export const OWN_EMAIL_REMOVAL_REASON = 'it is your own email'
export const PRIMARY_OWNER_REMOVAL_OMS_NOTE =
  'Only OMS can change that. Contact OMS to change who the primary owner is, or to remove them.'
export const OWN_EMAIL_REMOVAL_NOTE =
  'You cannot remove your own access from here.'

export const removePersonConfirmation = ({
  email,
  venueTitles,
  leftVenues,
} = {}) => {
  const who = String(email || 'This person').trim()
  const leaving = roleRows(venueTitles).filter(row => row.title)
  const leftRows = roleRows(leftVenues).filter(row => row.title)
  const venues = venueList(leaving.map(row => row.title))
  const change = venues
    ? `${who} will be removed from ${venues}.`
    : `${who} will not be removed.`
  const now = leaving
    .filter(row => row.currentRole)
    .map(row => `${row.title} is currently ${row.currentRole}.`)
  const left = leftRows.map(
    row =>
      `${row.title} will be left because ${
        row.reason || 'they are not on it'
      }.`,
  )
  const ownerNote = leftRows.some(
    row => row.reason === PRIMARY_OWNER_REMOVAL_REASON,
  )
    ? PRIMARY_OWNER_REMOVAL_OMS_NOTE
    : ''
  const ownEmailNote = leftRows.some(
    row => row.reason === OWN_EMAIL_REMOVAL_REASON,
  )
    ? OWN_EMAIL_REMOVAL_NOTE
    : ''
  const emailLine = venues
    ? 'One email is sent for the whole removal, not one per venue.'
    : 'No email is sent.'
  return [
    change,
    ...now,
    ...left,
    ownerNote,
    ownEmailNote,
    'A venue you do not tick is left.',
    emailLine,
  ]
    .filter(Boolean)
    .join(' ')
}

export const sharePeopleConfirmation = (people, venueTitles) => {
  const lines = (people || [])
    .map(person => {
      const name = String(person?.name || person?.email || 'This person').trim()
      const role = String(
        person?.roleLabel || person?.role || 'their current role',
      ).trim()
      return `${name} as ${role}`
    })
    .filter(Boolean)
  if (!lines.length) return 'Choose a person.'
  const who =
    lines.length === 1
      ? lines[0]
      : lines.length === 2
      ? `${lines[0]} and ${lines[1]}`
      : `${lines.slice(0, -1).join(', ')}, and ${lines.at(-1)}`
  const venues = (venueTitles || [])
    .map(title => String(title || '').trim())
    .filter(Boolean)
  const place = venues.length ? ` on ${personList(venues)}` : ''
  return `${who} will be shared${place}. A venue that already has them is left as it is.`
}

export const replaceProductConfirmation = ({ fromTitle, toTitle } = {}) => {
  const from = String(fromTitle || '').trim() || 'This product'
  const to = String(toTitle || '').trim() || 'the replacement'
  return `${from} will be replaced with ${to} on every venue in this group that has ${from}. Venues that do not have it are left as they are.`
}

const actionSentence = (action, groupName, menuName) => {
  if (action === 'invite_people')
    return `People will be invited to ${groupName}.`
  if (action === 'change_venue_person')
    return `This person’s access will be changed in ${groupName}.`
  if (action === 'dummy') return `A check will run on ${groupName}.`
  if (action === 'point_price_files') {
    return `Venues in ${groupName} will use the same price file.`
  }
  if (action === 'connect_supplier') {
    return `This supplier will be turned on for ${groupName}. Each venue keeps its own account number.`
  }
  if (action === 'apply_shopping_list') {
    return SHOPPING_LIST_REPLACES
  }
  if (action === 'copy_menu') {
    const name = menuName || 'Menu'
    return `${name} will be copied to ${groupName}, with its sections and dishes.`
  }
  if (action === 'copy_recipe_categories') {
    return `These category names will be copied to ${groupName}, not the dishes.`
  }
  const label = VENUE_GROUP_ACTION_LABELS[action] || 'This change'
  return `${label} will be copied to ${groupName}.`
}

const recipeTotals = recipeCounts => {
  const rows = recipeCounts || []
  const chosen = rows.reduce((sum, row) => sum + (Number(row.chosen) || 0), 0)
  const receiving = rows.reduce(
    (sum, row) => sum + (Number(row.receiving) || 0),
    0,
  )
  return { chosen, receiving }
}

export const menuDishesCannotLand = recipeCounts => {
  const { chosen, receiving } = recipeTotals(recipeCounts)
  return chosen > 0 && receiving === 0
}

export const menuSomeDishesCannotLand = recipeCounts => {
  const { chosen, receiving } = recipeTotals(recipeCounts)
  return receiving > 0 && chosen > receiving
}

export const menuConfirmBlocked = ({ noDishes, partial, allowWithoutDishes }) =>
  Boolean(noDishes) || (Boolean(partial) && !allowWithoutDishes)

export const showCopyWithoutDishes = ({ action, partial }) =>
  action === 'copy_menu' && Boolean(partial)

export const showMenuRecipeCounts = ({ partial, allowWithoutDishes }) =>
  !partial || Boolean(allowWithoutDishes)

export const alreadyHaveLines = ({ rows, counts, forceShare, onlyMissing }) => {
  const landingByVenue = new Map(
    (counts || []).map(row => [row.title, new Set(row.landing || [])]),
  )
  const lines = []
  for (const row of rows || []) {
    for (const name of row.recipes || []) {
      if (landingByVenue.get(row.title)?.has(name)) continue
      const replace =
        forceShare && !onlyMissing ? ' This copy will be replaced.' : ''
      lines.push(`${row.title} already has ${name}.${replace}`)
    }
  }
  return lines
}

export const missingRecipeLines = rows =>
  (rows || []).map(row => {
    const section = row.section || 'this menu'
    return `A dish on the ${section} section no longer exists and will be left off.`
  })

export const showOnlyMissingChoice = ({
  action,
  menusAlreadyThere,
  revenueAlreadyThere,
}) => {
  if (
    action === 'copy_allergen_flags' ||
    action === 'apply_shopping_list' ||
    action === 'copy_restricted_suppliers' ||
    action === 'replace_product' ||
    action === 'invite_people' ||
    action === 'change_venue_person'
  )
    return false
  if (action === 'assign_revenue_categories')
    return (revenueAlreadyThere || []).length > 0
  return action !== 'copy_menu' || (menusAlreadyThere || []).length > 0
}

export const onlyMissingLocked = ({ action, revenueAlreadyThere }) =>
  action === 'assign_revenue_categories' &&
  (revenueAlreadyThere || []).length > 0

export const menuAlreadyLines = (rows, onlyMissing) =>
  (rows || []).map(row =>
    onlyMissing
      ? `${row.title} already has ${row.name}.`
      : `${row.title} already has ${row.name}. Its sections will be replaced.`,
  )

export const allergenTakeSentence = ({ title, willTake, sourceName }) => {
  const venue = title || 'this venue'
  const source = sourceName || 'the source venue'
  const count = Number(willTake) || 0
  if (count <= 0)
    return `No products at ${venue} will take ${source}’s allergens.`
  const noun = count === 1 ? 'product' : 'products'
  return `${count} ${noun} at ${venue} will take ${source}’s allergens.`
}

const listedNames = names => {
  const shown = names.slice(0, 5)
  const extra = names.length - shown.length
  const parts = extra > 0 ? [...shown, `${extra} more`] : shown
  if (parts.length <= 1) return parts[0] || ''
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`
}

export const allergenOnlyHereLines = (rows, sourceName) => {
  const source = sourceName || 'the source venue'
  return (rows || []).flatMap(row => {
    const names = row.onlyHere || []
    if (names.length === 0) return []
    const venue = row.title || 'this venue'
    const count = names.length
    const noun = count === 1 ? 'product' : 'products'
    const verb = count === 1 ? 'is' : 'are'
    const their = count === 1 ? 'its' : 'their'
    return [
      `${count} ${noun} at ${venue} ${verb} not on ${source}, so ${their} allergens stay as they are. ${listedNames(
        names,
      )}.`,
    ]
  })
}

const clearingLine = (count, source, where) =>
  count === 1
    ? `1 product at ${source} has no allergens set. Copying will clear allergens on that product at ${where}.`
    : `${count} products at ${source} have no allergens set. Copying will clear allergens on those same products at ${where}.`

export const allergenClearingLines = (rows, sourceName) => {
  const source = sourceName || 'the source venue'
  const listed = (rows || []).filter(row => (row.clearing || []).length > 0)
  if (listed.length === 0) return []
  const counts = listed.map(row => row.clearing.length)
  if (listed.length > 1 && counts.every(count => count === counts[0])) {
    return [
      clearingLine(counts[0], source, `the other ${listed.length} venues`),
    ]
  }
  return listed.map(row =>
    clearingLine(row.clearing.length, source, row.title || 'this venue'),
  )
}

const missingHereLine = (count, source, where) => {
  const noun = count === 1 ? 'product' : 'products'
  const verb = count === 1 ? 'is' : 'are'
  return `${count} ${noun} on ${source} ${verb} not on ${where}. Nothing is written for them.`
}

export const allergenMissingHereLines = (rows, sourceName) => {
  const source = sourceName || 'the source venue'
  const listed = (rows || []).filter(row => (Number(row.missingHere) || 0) > 0)
  if (listed.length === 0) return []
  const counts = listed.map(row => Number(row.missingHere) || 0)
  if (listed.length > 1 && counts.every(count => count === counts[0])) {
    return [
      missingHereLine(counts[0], source, `the other ${listed.length} venues`),
    ]
  }
  return listed.map(row =>
    missingHereLine(
      Number(row.missingHere) || 0,
      source,
      row.title || 'this venue',
    ),
  )
}

export const allergenDetailLines = (rows, sourceName) => [
  ...allergenClearingLines(rows, sourceName),
  ...allergenOnlyHereLines(rows, sourceName),
  ...allergenMissingHereLines(rows, sourceName),
]

export const allergenEditedLine = ({ action, localChanges }) =>
  action === 'copy_allergen_flags' && (localChanges || []).length > 0
    ? 'Products this venue has edited keep their allergens unless you replace local changes.'
    : ''

export const allergenNothingWillCopy = ({ action, rows }) =>
  action === 'copy_allergen_flags' &&
  (rows || []).length > 0 &&
  (rows || []).every(row => (Number(row.willTake) || 0) === 0)

export const revenueNothingWillCopy = revenuePreview => {
  const rows = revenuePreview?.categories || []
  return rows.length === 0 || rows.every(row => (Number(row.venues) || 0) === 0)
}

export const bulkActionConfirmation = ({
  action,
  groupName,
  willChange,
  skipped,
  reasons,
  excludesSource,
  recipeSkips,
  recipeCounts,
  ingredientGaps,
  menuName,
  emptyShare,
  allowWithoutDishes,
  allergenPreview,
  sourceTitle,
  revenuePreview,
  restrictedPreview,
  fromTitle,
  toTitle,
  sharePeople,
  shareVenueTitles,
  inviteEmail,
  removePerson,
  changePerson,
}) => {
  if (emptyShare) return 'There are no categories to copy.'
  if (action === 'assign_revenue_categories') {
    if (revenueNothingWillCopy(revenuePreview))
      return 'Every venue would be left out.'
    return revenuePreview?.note || 'No revenue categories will be copied.'
  }
  if (action === 'copy_allergen_flags' && (allergenPreview || []).length > 0) {
    if (allergenNothingWillCopy({ action, rows: allergenPreview })) {
      return 'No products will take these allergens.'
    }
    return allergenPreview
      .map(row => {
        const willTake = Number(row.willTake) || 0
        const clearing = (row.clearing || []).length
        if (willTake > 0 && clearing >= willTake) return ''
        return allergenTakeSentence({
          title: row.title,
          willTake: Math.max(0, willTake - clearing),
          sourceName: sourceTitle,
        })
      })
      .filter(part => String(part || '').trim())
      .join(' ')
  }
  const total = (willChange || 0) + (skipped || 0)
  const why = (reasons || [])
    .map(
      row =>
        `${String(row.reason || 'Skipped').replace(/\.$/, '')} (${row.count})`,
    )
    .join('. ')
  const recipeSkipCount = Number(recipeSkips) || 0
  const rows = recipeCounts || []
  const missed = rows.map(row => (row.chosen || 0) - (row.receiving || 0))
  const sameAtEveryVenue =
    rows.length > 1 &&
    missed.every(count => count > 0 && count === missed[0]) &&
    missed[0] * rows.length === recipeSkipCount
  const venueSkipLine = skipped ? `${skipped} will be skipped. ${why}.` : ''
  const recipeSkipLine = sameAtEveryVenue
    ? `${missed[0]} recipes will be skipped at all ${rows.length} venues.`
    : recipeSkipCount
    ? recipeSkipSummary(ingredientGaps, excludesSource === true) ||
      `${recipeSkipCount} recipes will be skipped. Missing ingredients (${recipeSkipCount}).`
    : ''
  if (action === 'apply_shopping_list') return SHOPPING_LIST_REPLACES
  if (action === 'replace_product')
    return replaceProductConfirmation({ fromTitle, toTitle })
  if (action === 'invite_people' && inviteEmail?.email)
    return inviteEmailConfirmation(inviteEmail)
  if (action === 'change_venue_person' && removePerson?.email)
    return removePersonConfirmation(removePerson)
  if (action === 'change_venue_person' && changePerson?.email)
    return changePersonRoleConfirmation(changePerson)
  if (action === 'invite_people' && (sharePeople || []).length)
    return sharePeopleConfirmation(sharePeople, shareVenueTitles)
  if (action === 'copy_restricted_suppliers')
    return restrictedSupplierConfirmation(restrictedPreview)
  const menuLeavesDishesOff = action === 'copy_menu' && recipeSkipCount > 0
  const skipLine =
    action === 'invite_people'
      ? ''
      : menuLeavesDishesOff
      ? venueSkipLine
      : [venueSkipLine, recipeSkipLine].filter(Boolean).join(' ') ||
        'None will be skipped.'
  const count = willChange || 0
  const changeLine =
    count === 0
      ? '0 will get a copy.'
      : excludesSource
      ? count === 1
        ? 'Copied onto the other venue.'
        : `Copied onto the other ${count} venues.`
      : `You can update ${count} of ${total} venues.`
  const joinSentences = parts =>
    parts.filter(part => String(part || '').trim()).join(' ')
  const missingIngredientsSentence =
    'The other venues are missing ingredients, so these dishes cannot be copied yet.'
  const leadWithSkip = count === 0 && Boolean(skipped)

  if (action === 'copy_menu' && menuDishesCannotLand(rows)) {
    const name = menuName || 'this menu'
    const heldBack = excludesSource
      ? count === 1
        ? 'The other venue will not get the menu until those dishes can land.'
        : `The other ${count} venues will not get the menu until those dishes can land.`
      : 'These venues will not get the menu until those dishes can land.'
    return joinSentences([
      `None of the dishes on ${name} can land. ${missingIngredientsSentence}`,
      heldBack,
      skipLine,
    ])
  }
  if (action === 'copy_menu' && menuSomeDishesCannotLand(rows)) {
    const last = allowWithoutDishes
      ? 'The menu will be copied without those dishes.'
      : 'Nothing is copied until the box is ticked.'
    return joinSentences([
      `Some dishes will be left off. The other venues are missing ingredients for those dishes. ${last}`,
      skipLine,
    ])
  }
  return joinSentences(
    leadWithSkip
      ? [actionSentence(action, groupName, menuName), skipLine, changeLine]
      : [actionSentence(action, groupName, menuName), changeLine, skipLine],
  )
}
